/**
 * E57 · D-nav-fixes-2026-10-19 (founder Q-nav-1 A, Q-nav-8 A) — Caseload › Overview's filter bar and sorting, as data.
 *
 * ONE fetch (`caseloadOverview`, question `all`); the four questions are the server's per-row `signals` (its one
 * definition of each), so the browser only combines them: the chips are MULTI-select, combined as "any of" (a client
 * matching any chosen question shows); search matches the client's id or name (a hidden name too — hiding is about the
 * screen, not the search); the tool filter keeps the clients with that tool in use. Every column sorts. The whole state
 * lives in the URL, so a bookmark is a saved view: `?q=&signals=a,b&tool=&sort=&order=ascend|descend`.
 */
export const SIGNALS = ['homeworkBehind', 'scoreWentUp', 'noCheckIn7Days', 'diaryNotFilledThisWeek'] as const;
export type Signal = (typeof SIGNALS)[number];

export const SORT_KEYS = [
  'client',
  'therapist',
  'lastSession',
  'lastActivity',
  'homework',
  'score',
  'checkin',
  'diary',
  'tools',
] as const;
export type SortKey = (typeof SORT_KEYS)[number];
export type SortOrder = 'ascend' | 'descend';

export type CaseloadRow = {
  subject: string;
  view: boolean;
  therapist?: string | null;
  therapistName?: string | null;
  homeworkBehind?: number | null;
  latestScores?: Array<{ instrument: string; score: number; previous?: number | null; change?: number | null; wentUp: boolean }> | null;
  scoreWentUp: boolean;
  maxScoreRise?: number | null;
  checkInsInUse: boolean;
  lastCheckInDay?: string | null;
  daysSinceCheckIn?: number | null;
  diaryInUse: boolean;
  diaryDaysThisWeek?: number | null;
  lastSession?: { start: string; end?: string | null } | null;
  lastActivityAt?: string | null;
  toolsInUse?: string[] | null;
  signals: Record<Signal, boolean>;
};

export type CaseloadViewState = {
  q: string;
  signals: Signal[];
  tool: string | null;
  sort: SortKey | null;
  order: SortOrder | null;
};

const isSignal = (s: string): s is Signal => (SIGNALS as readonly string[]).includes(s);
const isSortKey = (s: string): s is SortKey => (SORT_KEYS as readonly string[]).includes(s);

export const viewStateOf = (params: URLSearchParams): CaseloadViewState => {
  const sort = params.get('sort') ?? '';
  const order = params.get('order');
  const known = isSortKey(sort) ? sort : null;
  return {
    q: params.get('q') ?? '',
    signals: (params.get('signals') ?? '').split(',').filter(isSignal),
    tool: params.get('tool') || null,
    sort: known,
    order: known ? (order === 'descend' ? 'descend' : 'ascend') : null,
  };
};

export const paramsOfView = (v: CaseloadViewState): URLSearchParams => {
  const p = new URLSearchParams();
  if (v.q) p.set('q', v.q);
  // the chips in their canonical order, whatever order they were clicked in — one URL per view
  const signals = SIGNALS.filter((s) => v.signals.includes(s));
  if (signals.length) p.set('signals', signals.join(','));
  if (v.tool) p.set('tool', v.tool);
  if (v.sort && v.order) {
    p.set('sort', v.sort);
    p.set('order', v.order);
  }
  return p;
};

export const isFiltered = (v: CaseloadViewState): boolean => !!v.q.trim() || v.signals.length > 0 || !!v.tool;

export type NameOf = (subject: string) => { firstName?: string | null; lastName?: string | null } | undefined;

const fullName = (nameOf: NameOf, subject: string): string => {
  const c = nameOf(subject);
  return [c?.firstName, c?.lastName].filter(Boolean).join(' ');
};

/** Search: the bare id (users/412 → "412") or any part of the full name, case-insensitive. */
export const matchesSearch = (row: CaseloadRow, q: string, nameOf: NameOf): boolean => {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const id = row.subject.replace(/^users\//, '');
  return id.includes(needle) || fullName(nameOf, row.subject).toLowerCase().includes(needle);
};

export const filterRows = (rows: readonly CaseloadRow[], v: CaseloadViewState, nameOf: NameOf): CaseloadRow[] =>
  rows.filter(
    (r) =>
      matchesSearch(r, v.q, nameOf) &&
      (v.signals.length === 0 || v.signals.some((s) => r.signals[s])) &&
      (!v.tool || (r.toolsInUse ?? []).includes(v.tool)),
  );

/** How many of ALL the clients answer each question — the chips' counts (Q-nav-8 A; inside Caseload only). */
export const signalCounts = (rows: readonly CaseloadRow[]): Record<Signal, number> =>
  Object.fromEntries(SIGNALS.map((s) => [s, rows.filter((r) => r.signals[s]).length])) as Record<Signal, number>;

/**
 * Each column's ascending order. A missing value (no session, not shared, not in use) sorts LAST ascending: "who did I
 * see longest ago" reads top-down. The two "needs a look" columns (Homework, Latest score) rank the bigger problem as the
 * larger value, so descending puts it first.
 */
const num = (v: number | null | undefined): number | null => (v === null || v === undefined ? null : v);
const time = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};

export const sortValue = (row: CaseloadRow, key: SortKey, nameOf: NameOf): string | number | null => {
  switch (key) {
    case 'client':
      return (fullName(nameOf, row.subject) || row.subject.replace(/^users\//, '')).toLowerCase();
    case 'therapist':
      return row.therapistName ? row.therapistName.toLowerCase() : null;
    case 'lastSession':
      return time(row.lastSession?.start);
    case 'lastActivity':
      return time(row.lastActivityAt);
    case 'homework':
      return row.view ? num(row.homeworkBehind) : null;
    case 'score':
      return row.view && row.latestScores?.length ? num(row.maxScoreRise) ?? 0 : null;
    case 'checkin':
      return row.view && row.checkInsInUse ? num(row.daysSinceCheckIn) ?? Number.MAX_SAFE_INTEGER : null;
    case 'diary':
      return row.view && row.diaryInUse ? num(row.diaryDaysThisWeek) ?? 0 : null;
    case 'tools':
      return row.view && row.toolsInUse ? row.toolsInUse.length : null;
    default:
      return null;
  }
};

/** antd's `sorter(a, b, sortOrder)` — it reverses for descend itself, so missing values are pinned last by flipping. */
export const compareBy =
  (key: SortKey, nameOf: NameOf) =>
  (a: CaseloadRow, b: CaseloadRow, order?: SortOrder | null): number => {
    const x = sortValue(a, key, nameOf);
    const y = sortValue(b, key, nameOf);
    const last = order === 'descend' ? -1 : 1;
    if (x === null && y === null) return a.subject.localeCompare(b.subject);
    if (x === null) return last;
    if (y === null) return -last;
    if (x < y) return -1;
    if (x > y) return 1;
    return a.subject.localeCompare(b.subject);
  };

/** The table's rows for a view: filtered, then sorted the way antd will show them (also what the count says). */
export const viewRows = (rows: readonly CaseloadRow[], v: CaseloadViewState, nameOf: NameOf): CaseloadRow[] => {
  const out = filterRows(rows, v, nameOf);
  if (!v.sort || !v.order) return out;
  const cmp = compareBy(v.sort, nameOf);
  const sign = v.order === 'descend' ? -1 : 1;
  return [...out].sort((a, b) => sign * cmp(a, b, v.order));
};
